import axios from 'axios';
import { Modal, ModalWrap } from 'ctls/modal/Modal';
import { usePromise } from 'hooks/usePromise';
import { useRefComponent } from 'hooks/useRefComponent';
import { tc } from 'language/i18nkey';
import { useZstdDlCenter } from 'pages/dlcenter/useZstdDlCenter';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';
import { useSWRConfig } from 'swr';
import { BRKS, STYLS } from 'util/variables';
import { IfDocSrchRes } from '../models/IfDocSrch';
import { DocInfo, DocInput } from './DocInput';
import { PopDocGrpSettTitle } from './PopDocGrpSettTitle';
import { TblCompareItem } from './TblCompareItem';
import { useApiDocGrp } from './useApiDocGrp';

/*
Item Id 정리. doc_grp_item_id 

ko-KR : 대한민국 
en : Global 
en-US : USA 

version  : 버전 
update : 업데이트( 업데이트 일자 비교 ? ) ==> 필요한지 확인 필요함. 
file  : 첨부파일 
expose : 공개상태 

*/

/**
 * 문서 그룹 설저.
 * 모바일, 데스크톱 팝업 레이아웃 다르게.
 */

type ShowProps = {
  docGrpId: number | undefined;
  docId: number | undefined; // 문서번호가 있으면, 이를 이용하여, 최초 설정문서 자동 선택.
};

type Props = {
  show(p: ShowProps): Promise<any>;
};

export type DocGrpItemId = 'ko-KR' | 'en' | 'en-US' | 'version' | 'update' | 'file' | 'expose' | 'disp-target';
export type DocGrpItemStat = 'Same' | 'Different';
export type DocGrpItem = {
  docGrpItemId: DocGrpItemId;
  docGrpId: number;
  docGrpItemStat: DocGrpItemStat;
};

export const PopDocGrpSett = forwardRef<Props, {}>((props, ref) => {
  const [visible, setVisible] = useState(false);
  const promise = usePromise<{}, { cmd?: string }>();
  const { mutate } = useSWRConfig();

  const [sels, setSels] = useState<DocGrpItemId[]>([]);
  const [docGrpId, setDocGrpId] = useState<number | undefined>(undefined);

  const { diffLevel } = useApiDocGrp(docGrpId);

  useImperativeHandle(ref, () => ({
    show: (p: ShowProps) => {
      return new Promise((resolve, reject) => {
        promise.current = { resolve, reject };
        setSelKoKr(null);
        setSelEn(null);
        setSelEnUs(null);
        setVisible(true);
        setDocGrpId(p.docGrpId);
        // 문서 그룹이 있으면, 해당 그룹으로 기본정보설정할 것.
        if (p.docGrpId) {
          // 문서 그룹이 있으면 문서그룹이 있으면, 문서그룹번호로 조회.
          axios
            .get<IfDocSrchRes[]>('/api/guest/zdata/ssqdoc/getDocSrchResByDocGrpId', {
              params: { docGrpId: p.docGrpId },
            })
            .then((res) => {
              const list = res.data;
              const koKr = list.find((item) => item.exposeSiteLanguage === 'ko-KR');
              const en = list.find((item) => item.exposeSiteLanguage === 'en');
              const enUs = list.find((item) => item.exposeSiteLanguage === 'en-US');
              setSelKoKr(koKr);
              setSelEn(en);
              setSelEnUs(enUs);
            });

          // 문서그룹번호로 선택 Item 조회.
          axios
            .get<{ docGrpItemId: DocGrpItemId }[]>('/api/member/zdata/docgrpitem/list', {
              params: { docGrpId: p.docGrpId },
            })
            .then((res) => {
              const list = res.data.map((item) => item.docGrpItemId);
              setSels(list);
            });
        } else if (p.docId) {
          axios.get('/api/guest/zdata/ssqdoc/getDocSrchResByDocId', { params: { docId: p.docId } }).then((res) => {
            const lang = res.data.exposeSiteLanguage;
            if (lang === 'ko-KR') {
              setSelKoKr(res.data);
            } else if (lang === 'en') {
              setSelEn(res.data);
            } else if (lang === 'en-US') {
              setSelEnUs(res.data);
            }
          });
        }
      });
    },
  }));

  const handleClose = () => {
    setVisible(false);
    promise.current.reject({ cmd: 'close' });
  };

  const [selKoKr, setSelKoKr] = useState<DocInfo | null>(null);
  const [selEn, setSelEn] = useState<DocInfo | null>(null);
  const [selEnUs, setSelEnUs] = useState<DocInfo | null>(null);

  const { triggerSrch } = useZstdDlCenter();

  const handleSave = () => {
    // promise.current.resolve({sels});
    //handleClose();
    // 1. 문서그룹이 없는 경우, 문서 그룹 등록.
    // 2. 문서그룹번호를 이용하여, 문서그룹문서 등록.
    // 3. 문서그룹 아이템 등록.

    Promise.resolve(docGrpId)
      .then((docGrpId) => {
        if (docGrpId) {
          return docGrpId;
        }
        return axios.post('/api/member/zdata/docgrp/save', {}).then((res) => res.data.docGrpId);
      })
      .then((docGrpId) => {
        console.log('docGrpId', docGrpId);
        // 그룹문서 등록.
        return axios
          .post('/api/member/zdata/docgrpitem/saves', {
            docGrpId,
            items: sels,
          })
          .then((res) => docGrpId);
      })
      .then((docGrpId) => {
        console.log('docGrpId', docGrpId);
        const list = [];

        if (selKoKr) {
          list.push({ docId: selKoKr.id, lang: 'ko-KR' });
        }
        if (selEn) {
          list.push({ docId: selEn.id, lang: 'en' });
        }
        if (selEnUs) {
          list.push({ docId: selEnUs.id, lang: 'en-US' });
        }

        // 그룹문서 아이템 등록.
        return axios
          .post('/api/member/zdata/docgrpmppng/saves', {
            docGrpId,
            list,
          })
          .then((res) => docGrpId);
      })
      .then((docGrpId) => {
        console.log('docGrpId', docGrpId);
        mutate(() => true);
        // dlcenter update.
        triggerSrch();
        handleClose();
      });
  };

  if (!visible) return null;

  return (
    <Modal onClick={(e) => e.stopPropagation()}>
      <Body>
        <PopDocGrpSettTitle onClose={handleClose} diffLevel={diffLevel} />
        <Cnts>
          <Left>
            <Guide>{tc('i18n_doc_grp_sett_desc')}</Guide>
            <DocInput label={tc('i18n_korea')} lang='ko-KR' sel={selKoKr} onChangeSel={setSelKoKr} />
            <DocInput label={tc('i18n_global')} lang='en' sel={selEn} onChangeSel={setSelEn} />
            <DocInput label={tc('i18n_usa')} lang='en-US' sel={selEnUs} onChangeSel={setSelEnUs} />
            <Tooltip
              id='tooltip-docinput'
              style={{
                backgroundColor: 'white',
                maxWidth: '25rem',
                color: '#2A2C2F',
                fontSize: '1.4rem',
                boxShadow: STYLS.boxShadow,
              }}
            />
          </Left>
          <Right>
            <Guide>{tc('i18n_doc_grp_sett_item_select')}</Guide>
            <TblCompareItem
              sels={sels}
              onChangeSels={setSels}
              idKoKr={selKoKr?.id}
              idEn={selEn?.id}
              idEnUs={selEnUs?.id}
            />
          </Right>
        </Cnts>
        <Bottom>
          <Btn onClick={handleSave}>{tc('i18n_save')}</Btn>
        </Bottom>
      </Body>
    </Modal>
  );
});

PopDocGrpSett.displayName = 'PopDocGrpSett';

export const usePopDocGrpSett = () => useRefComponent<Props>(PopDocGrpSett);

const Bottom = styled.div`
  padding: 0 3.2rem 2.3rem 3.2rem;
  text-align: center;
`;

const Guide = styled.div`
  font-size: 1.4rem;
  font-weight: 400;
  color: #2a2c2f;
  padding: 1.1rem 0;
`;

// const Scroll = styled.div`
//   overflow-y: auto;
//   flex-grow: 1;
// `;

const Cnts = styled.div`
  padding: 0 3.2rem 2.3rem 3.2rem;
  display: flex;
  flex-direction: row;
  height: 1px;
  flex-grow: 1;
  gap: 1.6rem;
  overflow-y: auto;
  @media (max-width: ${BRKS.mobileMax}) {
    flex-direction: column;
  }
`;

const Left = styled.div`
  flex-basis: 50%;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`;

const Right = styled.div`
  flex-basis: 50%;
`;

const Body = styled(ModalWrap)`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100rem;
  height: 56rem;
  @media (max-width: ${BRKS.mobileMax}) {
    max-width: 45rem;
    min-height: 64rem;
  }
`;

const Btn = styled.button`
  width: 100%;
  max-width: 29.6rem;
  height: 4.8rem;
  font-size: 1.6rem;
  font-weight: 700;
  background-color: #123069;
  color: #fff;
  border-radius: 10rem;
  line-height: 4.8rem;
`;
